import { Typography } from '@euler/app/components/typography';
import { PrimaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CoachingCaseBrowseRequest,
  CoachingLibraryCatalogue,
  CoachingNamedItem,
} from '@euler/model/coaching';
import { Fragment, memo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import {
  caseFilterSections,
  clearCaseFilters,
  toggleCaseFilter,
} from '../functions/caseFilters';
import { Divider } from './Divider';
import { ToggleChips } from './ToggleChips';

/**
 * Every catalog filter but category and search. Taps change a draft;
 * 完成 applies it, closing any other way drops it.
 */
export const CaseFilterSheet = memo(
  ({
    visible,
    request,
    catalogue,
    knownTags,
    onApply,
    onClose,
  }: {
    visible: boolean;
    /** The applied request, which the draft starts from at each opening. */
    request: CoachingCaseBrowseRequest;
    catalogue: CoachingLibraryCatalogue | null;
    /** Names for selected tags the catalogue does not offer. */
    knownTags: readonly CoachingNamedItem[];
    onApply: (draft: CoachingCaseBrowseRequest) => void;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [draft, setDraft] = useState(request);
    // Each opening starts over from what is applied.
    const [open, setOpen] = useState(visible);
    if (open !== visible) {
      setOpen(visible);
      if (visible) setDraft(request);
    }
    const sections = caseFilterSections(catalogue, draft.tags ?? [], knownTags);
    return (
      <BottomPanel visible={visible} onClose={onClose} tall>
        <View style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingHorizontal: rpx(16),
              paddingTop: rpx(18),
              paddingBottom: rpx(12),
              backgroundColor: aiColors.card,
              borderBottomWidth: 1,
              borderBottomColor: aiColors.border,
              // Above the list, so its shadow falls on the chips.
              zIndex: 1,
              shadowColor: aiColors.foreground,
              shadowOpacity: 0.08,
              shadowRadius: 6,
              shadowOffset: { width: 0, height: 2 },
              elevation: 3,
            }}
          >
            <Typography.Text size={rpx(17)} bold color={aiColors.foreground}>
              筛选
            </Typography.Text>
            <Pressable
              onPress={() => setDraft(clearCaseFilters)}
              accessibilityRole="button"
            >
              <Typography.Text size={rpx(14)} medium color={aiColors.primary}>
                清空
              </Typography.Text>
            </Pressable>
          </View>
          <ScrollView
            contentContainerStyle={{
              padding: rpx(16),
              // The first filter brings its own top margin.
              paddingTop: rpx(2),
              // Clears the floating 完成.
              paddingBottom: rpx(84),
            }}
          >
            {sections.map(section => {
              const selected = draft[section.key] ?? [];
              const onToggle = (value: string | number) =>
                setDraft(current =>
                  toggleCaseFilter(current, section.key, value),
                );
              const [only] = section.groups;
              return (
                <Fragment key={section.key}>
                  {section.divided ? <Divider /> : null}
                  {section.groups.length === 1 && only.title == null ? (
                    <ToggleChips
                      title={section.title}
                      options={only.options}
                      selected={selected}
                      onToggle={onToggle}
                    />
                  ) : (
                    <>
                      <Typography.Text
                        size={rpx(14)}
                        bold
                        color={aiColors.foreground}
                        style={{ marginTop: rpx(14) }}
                      >
                        {section.title}
                      </Typography.Text>
                      {section.groups.map(group => (
                        <ToggleChips
                          key={group.title}
                          title={group.title ?? ''}
                          level="group"
                          options={group.options}
                          selected={selected}
                          onToggle={onToggle}
                        />
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })}
          </ScrollView>
          <View
            style={{
              position: 'absolute',
              left: rpx(16),
              right: rpx(16),
              bottom: rpx(16),
              borderRadius: rpx(11),
              shadowColor: aiColors.primary,
              shadowOpacity: 0.3,
              shadowRadius: 12,
              shadowOffset: { width: 0, height: 4 },
              elevation: 6,
            }}
          >
            <PrimaryButton title="完成" onPress={() => onApply(draft)} />
          </View>
        </View>
      </BottomPanel>
    );
  },
);
